import { useCallback, useEffect, useLayoutEffect, type RefObject } from "react";

export interface AutoGrowOptions {
  /** Height while empty or short, in lines. */
  minRows?: number;
  /** Height at which the field stops growing and scrolls, in lines. */
  maxRows?: number;
  /**
   * Share of the window the field may take, so on a short screen it never
   * pushes the controls around it out of view. Wins over `maxRows`.
   */
  maxViewportShare?: number;
}

const FALLBACK_LINE_RATIO = 1.5;

function px(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Sizes a textarea to its content between `minRows` and `maxRows`.
 *
 * Bounds are whole lines plus padding, so the field never stops partway
 * through a line: a half-visible line reads as text that has been cut off
 * rather than as text that scrolls. The scrollbar only appears once the field
 * is at its maximum, because a scrollbar on a field that could simply grow
 * hides what the user typed for no reason.
 */
export function resizeTextarea(
  element: HTMLTextAreaElement,
  { minRows = 2, maxRows = 10, maxViewportShare = 0.4 }: AutoGrowOptions = {},
): void {
  const style = window.getComputedStyle(element);
  const fontSize = px(style.fontSize) || 14;
  const line = px(style.lineHeight) || fontSize * FALLBACK_LINE_RATIO;
  const padding = px(style.paddingTop) + px(style.paddingBottom);
  const border = px(style.borderTopWidth) + px(style.borderBottomWidth);
  const borderBox = style.boxSizing === "border-box";
  // Heights set below are in the element's own box model.
  const frame = borderBox ? padding + border : 0;

  const viewportRows = Math.floor((window.innerHeight * maxViewportShare - frame) / line);
  const rows = Math.max(minRows, Math.min(maxRows, viewportRows));
  const min = line * minRows + frame;
  const max = line * rows + frame;

  // Collapsed first, so scrollHeight measures the content rather than the
  // height the field already has; otherwise it could never shrink.
  element.style.height = "auto";
  // scrollHeight includes padding but never border.
  const content = borderBox ? element.scrollHeight + border : element.scrollHeight - padding;
  const height = Math.min(max, Math.max(min, content));

  element.style.height = `${height}px`;
  element.style.overflowY = content > max ? "auto" : "hidden";
}

/**
 * Keeps a textarea sized to `value` as it changes, and to the window as it
 * resizes. Runs before paint, so the field never flashes at the wrong height.
 */
export function useAutoGrowTextarea(
  ref: RefObject<HTMLTextAreaElement>,
  value: string,
  { minRows = 2, maxRows = 10, maxViewportShare = 0.4 }: AutoGrowOptions = {},
): void {
  const resize = useCallback(() => {
    if (ref.current) resizeTextarea(ref.current, { minRows, maxRows, maxViewportShare });
  }, [ref, minRows, maxRows, maxViewportShare]);

  useLayoutEffect(resize, [resize, value]);

  useEffect(() => {
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [resize]);
}
